import * as React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  InputLabelProps as MuiInputLabelProps,
} from '@mui/material';
import { SelectProps as MuiSelectProps, SelectChangeEvent } from '@mui/material/Select';
import {
  selectFieldFormControl,
  selectFieldHelperText,
  selectFieldLabel,
} from 'ui/SelectField/selectFieldCssDefs';
import Select from 'ui/Select';

export interface SelectFieldProps {
  className?: string;
  disabled?: boolean;
  label?: string;
  labelAdornment?: React.ReactNode;
  fullWidth?: boolean;
  helperText?: string;
  InputLabelProps?: MuiInputLabelProps;
  error?: boolean;
  SelectProps?: MuiSelectProps;
  children: React.ReactNode;
  name?: string;
  id?: string;
  value?: string | string[];
  onChange?: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: React.FocusEventHandler<any>;
}

/**
 * Use `SelectField` for rendering an input where you can choose one of multiple choices, see stories for more complete examples. This is a wrapper for Select
 * that includes FormControl, InputLabel, and FormHelperText
 *
 * ```js
 * import { SelectField } from 'ui';
 * ```
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const SelectField: React.FunctionComponent<React.PropsWithChildren<SelectFieldProps>> = ({
  label,
  labelAdornment,
  disabled,
  fullWidth,
  SelectProps,
  children,
  helperText,
  InputLabelProps = {},
  error,
  className,
  name,
  id,
  value,
  onChange,
  onBlur,
}) => {
  return (
    <FormControl
      className={className}
      css={selectFieldFormControl(fullWidth ?? false)}
      variant="outlined"
      error={error}
      disabled={disabled}
    >
      {label && (
        <InputLabel
          {...InputLabelProps}
          css={selectFieldLabel}
          htmlFor={id ? id : SelectProps?.inputProps?.id}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span>{label}</span>
            {labelAdornment}
          </span>
        </InputLabel>
      )}
      <Select
        fullWidth={fullWidth}
        label={label}
        {...SelectProps}
        disabled={disabled ?? SelectProps?.disabled}
        name={name ?? SelectProps?.inputProps?.name}
        id={id ?? SelectProps?.inputProps?.id}
        value={value ?? SelectProps?.value}
        onChange={onChange ?? SelectProps?.onChange}
        onBlur={onBlur ?? SelectProps?.onBlur}
      >
        {children}
      </Select>
      {helperText && <FormHelperText css={selectFieldHelperText}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default SelectField;
